import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Step,
  StepLabel,
  Stepper,
} from '@material-ui/core'
import { SelectNonprofit, useSelectNonprofit } from './select-nonprofit'
import {
  initialValues,
  DonateDetails,
  useDonateDetails,
} from './donate-details'
import { switchCase } from '../../utils'
import { useDonate } from './donate.hook'
import { hasNonprofitWebsite, removeFirstCase } from './donate.utils'
import { DonateProps } from './donate.models'
import { ItemImages } from './item-images'
import { DonatePublishedSummary } from './donate-published-summary'
import { useTranslation } from 'react-i18next'
import {
  Link as ReactRouterLink,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { Alert } from '@material-ui/lab'
import { ImageObject } from './item-images/item-images.hook'
import { nonprofitsApi } from '../../services/api/nonprofits'
import { trackException } from '../../store/app-insights/reducer'
import { AxiosError } from 'axios'
import { PageWrapper } from '../../components/page-wrapper'
import {
  ExternalSiteLinkBar,
  resolveNonprofitWebsiteHref,
} from '../../shared/external-site-link-bar'
import { BreadcrumbItem } from '../../components/breadcrumbs'
import { AppDialog } from '../../hooks/use-app-dialog/use-app-dialog.component'
import { ShippingAddressForm } from '../../components/shipping-address-form/shipping-address-form'

export const Donate: React.FC<DonateProps> = ({
  categoryRequest,
  nonprofitRequest,
  nonprofitIdProp,
  createListedItemApi,
  checkedAuth,
  userStatus,
}): React.ReactElement => {
  const {
    classes,
    activeStep,
    steps,
    nonprofitId,
    handleNext,
    handleBack,
    handleReset,
    loadStatus,
    setAppDialog,
    createListedItem,
    publishedItemId,
    shippingAddressExists,
    isValidAddress,
    validateParcel,
    parcelData,
    setIsShow,
    /*@ts-ignore*/
  } = useDonate({ nonprofitIdProp, createListedItemApi })
  const params = useParams<{ nonprofitId: string }>()
  const selectNonprofit = useSelectNonprofit({
    categoryRequest,
    nonprofitRequest,
  })
  const navigate = useNavigate()
  const { donateDetailsFormValid, ...donateDetailsProps } = useDonateDetails()
  const [imageItems, setImageItems] = useState<ImageObject[]>([])
  const [visibleDraftButton, setVisibleDraftButton] = useState(false)
  const [addAddressButtonClicked, setAddAddressButtonClicked] = useState(false)
  const { t } = useTranslation()

  const donateBreadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('donation.breadcrumbHome'), to: '/' },
      { label: t('header.menu.donate') },
    ],
    [t]
  )

  /* Set Nonprofit id if there are NonprofitExternalId */
  useEffect(() => {
    if (params && params.nonprofitId) {
      const nonprofit = selectNonprofit.nonprofits.filter(
        (nonprofit) => nonprofit.nonprofitId.toString() === params.nonprofitId
      )[0]
      const getInfoByExternalId = async () => {
        try {
          const data = await nonprofitsApi
            .getInfoByExternalId(nonprofit.externalId)
            .then((data) => data)
          selectNonprofit.setSelectedNonprofit({
            nonprofitId: data.nonProfitId,
            name: data.name,
            nonprofitCategoryId: data.categoryId,
            selected: data.nonProfitId,
            website: data.webSite,
          })
        } catch (e) {
          const { response } = e as AxiosError
          const code = response?.status || 500
          if (code === 404) {
            setAppDialog({
              message: t('donation.externalNotFound.message'),
              title: t('donation.externalNotFound.title'),
              isShow: true,
              variant: 'warning',
              closeHandler: () => {
                /* Hard reload */
                window.location.href = '/donate'
              },
              actions: (
                <>
                  <Button onClick={() => (window.location.href = '/donate')}>
                    {t('donation.externalNotFound.ok')}
                  </Button>
                </>
              ),
            })
          } else {
            setAppDialog({
              message: t('donation.externalLoadError.message'),
              title: t('donation.externalLoadError.title'),
              isShow: true,
              variant: 'error',
              closeHandler: () => {
                /* Hard reload */
                setIsShow(false)
              },
              actions: (
                <>
                  <Button onClick={() => window.location.reload()}>
                    {t('donation.externalLoadError.reload')}
                  </Button>
                </>
              ),
            })
            trackException({
              e,
            })
          }
        }
      }

      getInfoByExternalId()
    }
  }, [])

  const imageIds = useMemo(
    () => imageItems.map(({ id }) => String(id)),
    [imageItems]
  )

  const setImageIds = (imageList: ImageObject[]) => {
    setImageItems(imageList)
  }

  const stepperCases = (nonprofitId: number | string) => {
    const cases = {
      '0': <SelectNonprofit {...selectNonprofit} />,
      '1': <DonateDetails {...donateDetailsProps} />,
      '2': (
        <ItemImages
          setItemImages={setImageIds}
          onErrorHandler={(message) =>
            setAppDialog({ isShow: true, variant: 'error', message })
          }
        />
      ),
    }
    return nonprofitId ? removeFirstCase(cases) : cases
  }

  const navigateToCreatedItem = () => {
    navigate(`/listed-items/${publishedItemId}`)
  }

  const toInitialDonateState = () => {
    handleReset()
    const { setSelectedCategory, setSelectedNonprofit } = selectNonprofit
    const { setDonateDetails } = donateDetailsProps
    setSelectedNonprofit()
    setSelectedCategory()
    setImageItems([])
    setDonateDetails({ ...initialValues })
  }

  useEffect(() => {
    isAvailableSaveDraft()
  }, [donateDetailsProps.donateDetails])

  useEffect(() => {
    if (parcelData) {
      handleNext()
    }
  }, [parcelData])

  const isAvailableSaveDraft = () => {
    const { title } = donateDetailsProps.donateDetails
    setVisibleDraftButton(!!title)
  }

  const isNextButtonDisabled = (nonprofitId: string | number): boolean => {
    const cases = {
      '0': !selectNonprofit.selectedNonprofit?.name,
      '1': donateDetailsFormValid,
      '2': false,
    }

    return switchCase(nonprofitId ? removeFirstCase(cases) : cases)(true)(
      activeStep.toString()
    )
  }

  /*Stepper content*/
  const stepperContent = (nonprofitId: number | string) =>
    switchCase(stepperCases(nonprofitId))('Unknown stepIndex')(
      activeStep.toString()
    )
  const nextButtonIsDisabled = () => {
    const { width, height, length, weight } = donateDetailsProps.donateDetails

    const shippingInvalid =
      !(width && height && length && weight) && activeStep === steps.length - 2
    return (
      isNextButtonDisabled(nonprofitId) ||
      loadStatus.loading ||
      (activeStep === steps.length - 1 && (!userStatus || !imageIds.length)) ||
      shippingInvalid
    )
  }

  /*Is Disabled Prev Btn*/
  const isDisabledPrevBtn = () => activeStep === 0

  const handleClick = (activeStep: number) => {
    if (activeStep === 1) {
      validateParcel(donateDetailsProps.donateDetails)
    } else {
      handleNext()
    }
  }

  return (
    <>
      <ShippingAddressForm
        addAddressButtonClicked={addAddressButtonClicked}
        setAddAddressButtonClicked={setAddAddressButtonClicked}
        onClose={() => setAddAddressButtonClicked(false)}
      />
      <PageWrapper
        title={t('donation.pageTitle')}
        displayGoBackButton={true}
        goBackClassName={classes.goBackButton}
        secondaryHeaderClassName={classes.secondaryHeaderClassName}
        breadcrumbItems={donateBreadcrumbItems}
      >
        {loadStatus.loadedSuccess && (
          <div className={classes.publishSuccessBanner}>
            {loadStatus.isDraft ? (
              t('donation.successDraft')
            ) : (
              <>
                {t('donation.success')}
                <> </>
                {t('donation.successModerationNote')}
              </>
            )}
          </div>
        )}
        <div>
          {(!shippingAddressExists || !isValidAddress) && (
            <div className={classes.shippingAddressAlert}>
              <Alert severity="warning">
                {!shippingAddressExists
                  ? t('checkout.addAddresssLabel')
                  : t('checkout.shippingAddressInvalid')}{' '}
                <b>
                  <Button
                    color="primary"
                    className={classes.addAddressButton}
                    variant="text"
                    onClick={() => setAddAddressButtonClicked(true)}
                  >
                    {t('checkout.addAddressLink')}
                  </Button>
                </b>
              </Alert>
            </div>
          )}
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            className={classes.stepperOffset}
          >
            {steps.map((label, index) => {
              const isLastStep = index === steps.length - 1
              const isImagesStepCompleted = isLastStep && imageIds.length > 0
              return (
                <Step
                  key={label}
                  completed={isImagesStepCompleted || index < activeStep}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              )
            })}
          </Stepper>
          {checkedAuth ? (
            <div>
              <div>
                <div className={classes.inputHolder}>
                  {!loadStatus.loaded &&
                    activeStep !== steps.length &&
                    stepperContent(nonprofitId)}
                  {selectNonprofit.selectedNonprofit?.name &&
                    !loadStatus.loaded && (
                      <ExternalSiteLinkBar
                        dividerAbove
                        href={resolveNonprofitWebsiteHref(
                          selectNonprofit.selectedNonprofit
                        )}
                      >
                        {selectNonprofit.selectedNonprofit?.name}
                      </ExternalSiteLinkBar>
                    )}
                  {loadStatus.loaded && (
                    <DonatePublishedSummary
                      donateDetails={donateDetailsProps.donateDetails}
                      nonprofitName={selectNonprofit.selectedNonprofit?.name}
                      nonprofitWebsite={
                        selectNonprofit.selectedNonprofit?.website
                      }
                      isDraft={loadStatus.isDraft}
                    />
                  )}
                </div>
                {loadStatus.loaded && (
                  <div className={classes.loadedBtnHolder}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={toInitialDonateState}
                    >
                      {t('donation.addNewButton')}
                    </Button>
                    {!loadStatus.isDraft && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={navigateToCreatedItem}
                      >
                        {t('donation.showListedItemButton')}
                      </Button>
                    )}
                  </div>
                )}
                {!loadStatus.loaded && (
                  <div className={classes.btnHolder}>
                    {visibleDraftButton && (
                      <Button
                        className={`${classes.btnDraft}`}
                        variant="contained"
                        /*@ts-ignore*/
                        onClick={() =>
                          createListedItem(
                            { ...donateDetailsProps.donateDetails },
                            selectNonprofit.selectedNonprofit?.nonprofitId ?? 0,
                            imageIds,
                            true
                          )
                        }
                      >
                        {t('donation.saveDraft')}
                      </Button>
                    )}
                    <Button disabled={isDisabledPrevBtn()} onClick={handleBack}>
                      {t('donation.back')}
                    </Button>
                    <FormControl error={true}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          activeStep === steps.length - 1
                            ? createListedItem(
                                { ...donateDetailsProps.donateDetails },
                                selectNonprofit.selectedNonprofit
                                  ?.nonprofitId ?? 0,
                                imageIds,
                                false
                              )
                            : handleClick(activeStep)
                        }}
                        disabled={nextButtonIsDisabled()}
                      >
                        {activeStep === steps.length - 1
                          ? t('donation.publish')
                          : t('donation.next')}
                      </Button>
                      {activeStep === steps.length - 1 && !userStatus && (
                        <FormHelperText>
                          {t('donation.pleaseActivateYourAccount')}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <CircularProgress />
          )}
        </div>
      </PageWrapper>
    </>
  )
}
